import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import CircularLoader from 'components/CircularLoader';
import { InfoOutlined } from 'components/icons';
import NestedTable, { NestedColumn } from 'components/tables/list-table/NestedTable';
import { Alert } from 'components/ui/alert';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useGetExpensesWithFiltersQuery } from 'store/api/expenses';
import { ClassifierData } from 'types/classifiers';
import { ExpenseEmployeeGroup, ExpenseListItem, ExpenseTypeGroup } from 'types/expenses';
import { t } from 'utils/getTranslations';
import { displayName, useLastNameFirst } from 'utils/nameOrder';
import ExpensesGroupedTier3Rows from './ExpensesGroupedTier3Rows';
import {
    GROUPED_EXPAND_COLUMN_ID,
    GROUPED_EXPAND_COLUMN_WIDTH,
    GROUPED_TYPE_COLUMN_ID,
    GROUPED_TYPE_COLUMN_WIDTH,
    groupedAmountColumnId,
    groupedMirroredWidth,
} from './expensesGroupedColumns';

type Props = {
    typeGroup: ExpenseTypeGroup;
    currencyCodes: string[];
    expenseStatusClassifier?: ClassifierData;
    onDuplicate: (expense: ExpenseListItem) => void;
    /**
     * Per-(typeCode, employeeId) expansion state lifted to the Tier 1 parent so Tier 2
     * collapse-and-reopen restores the prior expansion instead of remounting empty.
     */
    isEmployeeExpanded: (employeeId: number) => boolean;
    onToggleEmployee: (employeeId: number) => void;
    /**
     * The active RSQL filter the list table built from the user's main/quick/passive filters.
     * Composed with `expenseTypeCode==X;employee.id==Y` when lazy-fetching Tier 3 lines so the
     * lines stay scoped to whatever the user currently has filtered.
     */
    activeFilter: string;
};

/**
 * Composes the Tier 3 RSQL filter for one (typeCode, employeeId) pair, AND'd with the user's
 * active filter. Empty active filter → just the two clauses. Order doesn't matter — RSQL `;`
 * is commutative AND, so a user filter that already constrains expenseTypeCode just gets a
 * redundant clause rather than a conflicting one.
 */
const composeTier3Filter = (activeFilter: string, typeCode: string, employeeId: number): string =>
    [activeFilter, `expenseTypeCode==${typeCode}`, `employee.id==${employeeId}`]
        .filter((s) => s && s.length > 0)
        .join(';');

const TIER_3_PAGE_SIZE = 200;

type Tier3LinesProps = {
    typeCode: string;
    employeeId: number;
    activeFilter: string;
    expenseStatusClassifier?: ClassifierData;
    onDuplicate: (expense: ExpenseListItem) => void;
};

/**
 * Renders the Tier 3 expense lines for one expanded (typeCode, employeeId) pair. Mounted only
 * while the row is expanded, so it gets the lazy-fetch behavior for free — RTK Query's cache
 * (default keepUnusedDataFor: 60s) keeps re-expansions instant even after unmount.
 */
const Tier3Lines = ({ typeCode, employeeId, activeFilter, expenseStatusClassifier, onDuplicate }: Tier3LinesProps) => {
    const filters = composeTier3Filter(activeFilter, typeCode, employeeId);
    const { data, isFetching } = useGetExpensesWithFiltersQuery({
        filters,
        sort: 'expenseDate,desc',
        pageSize: TIER_3_PAGE_SIZE,
        page: 0,
    });

    if (isFetching && !data) {
        return <CircularLoader className="static py-4 [&>span]:size-6" />;
    }

    return (
        <div className="flex flex-col gap-2">
            {data && data.totalElements > TIER_3_PAGE_SIZE ? (
                // The Tier 3 panel is hard-capped at TIER_3_PAGE_SIZE so the browser doesn't choke
                // on a 10k-row bucket. Surface the cap explicitly so users don't silently miss
                // lines and can't reconcile the Tier 2 aggregate with what they see here.
                <Alert className="text-fg bg-info/8 flex items-center gap-2 rounded-lg border-transparent py-0.5 pr-4 pl-3 font-semibold">
                    <span className="flex w-6 shrink-0 justify-center">
                        <InfoOutlined className="text-info size-4" />
                    </span>
                    <div className="min-w-0">
                        {t('expenses.grouped.tier3-truncated', {
                            shown: String(TIER_3_PAGE_SIZE),
                            total: String(data.totalElements),
                        })}
                    </div>
                </Alert>
            ) : null}
            <ExpensesGroupedTier3Rows
                expenses={data?.content ?? []}
                expenseStatusClassifier={expenseStatusClassifier}
                onDuplicate={onDuplicate}
            />
        </div>
    );
};

/**
 * Tier 2 of the grouped Expenses view: a per-employee breakdown for one expense-type group.
 * Each row is itself expandable into Tier 3 (individual expense lines, in their own
 * inner table because Tier 3 has a different column structure).
 *
 * Tier 3 lines are lazy-fetched per (typeCode, employeeId) pair on demand — the BE returns
 * only aggregates here, so we hit the paginated /v1/expenses endpoint when the user expands
 * a Tier 2 row.
 *
 * Column widths mirror the Tier 1 outer table — the parent Tier 1 row's TableCell colSpan fills the
 * table width, and each column pins itself to the `--utw-w-*` variable Tier 1's managed layout
 * publishes for the column of the same id, so `tableLayout: fixed` lines the currency columns up
 * vertically with their Tier 1 counterparts at any width.
 */
const ExpensesGroupedTier2Table = ({
    typeGroup,
    currencyCodes,
    expenseStatusClassifier,
    onDuplicate,
    isEmployeeExpanded,
    onToggleEmployee,
    activeFilter,
}: Props) => {
    const lastNameFirst = useLastNameFirst();
    // Flip to "Last First" under LAST_NAME_FIRST when the BE supplied the separate parts; falls back
    // to the pre-joined employeeFullName otherwise.
    const formatName = useCallback(
        (employee: ExpenseEmployeeGroup) =>
            displayName(
                {
                    name: employee.employeeFullName,
                    firstName: employee.employeeFirstName,
                    lastName: employee.employeeLastName,
                },
                lastNameFirst
            ),
        [lastNameFirst]
    );
    const columns = useMemo<NestedColumn<ExpenseEmployeeGroup>[]>(
        () => [
            {
                id: 'employee',
                header: <FormattedMessage id="expenses.added-by" />,
                width: groupedMirroredWidth(GROUPED_TYPE_COLUMN_ID, GROUPED_TYPE_COLUMN_WIDTH, true),
                Cell: (employee) => <p className="text-body1 font-medium">{formatName(employee)}</p>,
            },
            ...currencyCodes.map<NestedColumn<ExpenseEmployeeGroup>>((code) => ({
                id: groupedAmountColumnId(code),
                header: <FormattedMessage id="expenses.grouped.amount-in" values={{ currency: code }} />,
                width: groupedMirroredWidth(groupedAmountColumnId(code), 'auto'),
                align: 'right',
                sortAccessor: (employee) => employee.amountsByCurrency?.[code] ?? null,
                Cell: (employee) => {
                    const value = employee.amountsByCurrency[code];
                    return value != null ? <MoneyDisplay amount={value} currency={code} /> : <>-</>;
                },
            })),
        ],
        [currencyCodes, formatName]
    );

    return (
        <NestedTable<ExpenseEmployeeGroup>
            data={typeGroup.employees}
            columns={columns}
            getRowKey={(employee) => employee.employeeId}
            isRowExpanded={(employee) => isEmployeeExpanded(employee.employeeId)}
            onToggleRow={(employee) => onToggleEmployee(employee.employeeId)}
            expandColumnWidth={groupedMirroredWidth(GROUPED_EXPAND_COLUMN_ID, GROUPED_EXPAND_COLUMN_WIDTH, true)}
            expandRowAriaLabel={(employee, isExpanded) =>
                t(isExpanded ? 'expenses.grouped.collapse-employee-row' : 'expenses.grouped.expand-employee-row', {
                    label: formatName(employee),
                })
            }
            renderExpandedContent={(employee) => (
                <Tier3Lines
                    typeCode={typeGroup.expenseTypeCode}
                    employeeId={employee.employeeId}
                    activeFilter={activeFilter}
                    expenseStatusClassifier={expenseStatusClassifier}
                    onDuplicate={onDuplicate}
                />
            )}
        />
    );
};

export default ExpensesGroupedTier2Table;
